import {
  ComputeBudgetProgram,
  Connection,
  Keypair,
  PublicKey,
  Transaction,
  TransactionInstruction,
  TransactionMessage,
  VersionedTransaction,
} from "@solana/web3.js";
import type { AnyTx, ConnectedWallet } from "./wallet";

export interface FeeSettings {
  computeUnitLimit: number;
  computeUnitPriceMicroLamports: number;
}

export const DEFAULT_FEES: FeeSettings = {
  computeUnitLimit: 600_000,
  computeUnitPriceMicroLamports: 0,
};

export function budgetIxs(fees: FeeSettings): TransactionInstruction[] {
  const out = [ComputeBudgetProgram.setComputeUnitLimit({ units: fees.computeUnitLimit })];
  if (fees.computeUnitPriceMicroLamports > 0) {
    out.push(
      ComputeBudgetProgram.setComputeUnitPrice({
        microLamports: fees.computeUnitPriceMicroLamports,
      })
    );
  }
  return out;
}

export class SimulationError extends Error {
  readonly logs: string[];
  constructor(message: string, logs: string[]) {
    super(message);
    this.logs = logs;
  }
}

/**
 * Build, simulate, sign and send. Simulation happens BEFORE the wallet popup:
 * a transaction that cannot succeed should never reach the point where the user
 * is asked to approve it.
 */
export async function buildSimulateSend(opts: {
  connection: Connection;
  wallet: ConnectedWallet;
  instructions: TransactionInstruction[];
  fees: FeeSettings;
  extraSigners?: Keypair[];
  label: string;
  onProgress?: (msg: string) => void;
}): Promise<{ signature: string; unitsConsumed: number | null }> {
  const { connection, wallet, instructions, fees, extraSigners = [], label, onProgress } = opts;

  const bh = await connection.getLatestBlockhash("confirmed");
  const tx = new Transaction().add(...budgetIxs(fees), ...instructions);
  tx.recentBlockhash = bh.blockhash;
  tx.feePayer = wallet.publicKey;

  onProgress?.(`${label}: simulating`);
  const sim = await connection.simulateTransaction(tx);
  if (sim.value.err) {
    throw new SimulationError(
      `${label} would fail on-chain: ${JSON.stringify(sim.value.err)}`,
      sim.value.logs ?? []
    );
  }

  onProgress?.(`${label}: awaiting wallet approval`);
  if (extraSigners.length) tx.partialSign(...extraSigners);
  const [signed] = await wallet.signAllTransactions([tx]);
  if (extraSigners.length && signed instanceof Transaction) {
    // The wallet round-trips the transaction, so re-apply the ephemeral
    // signatures it does not know about (e.g. a freshly generated mint account).
    signed.partialSign(...extraSigners);
  }

  onProgress?.(`${label}: sending`);
  const raw =
    signed instanceof VersionedTransaction
      ? signed.serialize()
      : signed.serialize({ requireAllSignatures: true, verifySignatures: false });
  const signature = await connection.sendRawTransaction(raw, {
    preflightCommitment: "confirmed",
    skipPreflight: false,
  });

  onProgress?.(`${label}: confirming`);
  await connection.confirmTransaction(
    { signature, blockhash: bh.blockhash, lastValidBlockHeight: bh.lastValidBlockHeight },
    "confirmed"
  );
  const status = await connection.getSignatureStatus(signature, {
    searchTransactionHistory: true,
  });
  if (status.value?.err) {
    throw new Error(`${label} failed on-chain: ${JSON.stringify(status.value.err)}`);
  }
  return { signature, unitsConsumed: sim.value.unitsConsumed ?? null };
}

/** Signs and sends transactions the Raydium SDK produced for us. */
export async function sendPrebuilt(
  connection: Connection,
  wallet: ConnectedWallet,
  txs: AnyTx[]
): Promise<string[]> {
  const signed = await wallet.signAllTransactions(txs);
  const sigs: string[] = [];
  for (const s of signed) {
    const raw =
      s instanceof VersionedTransaction
        ? s.serialize()
        : s.serialize({ requireAllSignatures: true, verifySignatures: false });
    const sig = await connection.sendRawTransaction(raw, {
      preflightCommitment: "confirmed",
      skipPreflight: false,
    });
    const bh = await connection.getLatestBlockhash("confirmed");
    await connection.confirmTransaction(
      { signature: sig, blockhash: bh.blockhash, lastValidBlockHeight: bh.lastValidBlockHeight },
      "confirmed"
    );
    sigs.push(sig);
  }
  return sigs;
}

export async function readTokenBalanceRaw(
  connection: Connection,
  ata: PublicKey
): Promise<bigint> {
  const info = await connection.getAccountInfo(ata, "confirmed");
  if (!info || info.data.length < 72) return 0n;
  // SPL token account layout: amount is a little-endian u64 at offset 64.
  return new DataView(info.data.buffer, info.data.byteOffset + 64, 8).getBigUint64(0, true);
}

/**
 * The Raydium SDK's execute() resolves as soon as the transaction is confirmed
 * as *landed*, which is not the same as *succeeded*: a transaction that failed
 * its program logic still lands, and the SDK returns its signature happily.
 * Reporting that as success is how a UI ends up saying "swap confirmed" after a
 * swap that moved nothing. Every SDK-driven send is checked through here.
 */
export async function assertTxSucceeded(
  connection: Connection,
  signature: string,
  label: string
): Promise<void> {
  for (let i = 0; i < 20; i++) {
    const st = await connection.getSignatureStatus(signature, {
      searchTransactionHistory: true,
    });
    const v = st.value;
    if (v?.err) {
      const tx = await connection
        .getTransaction(signature, { commitment: "confirmed", maxSupportedTransactionVersion: 0 })
        .catch(() => null);
      const logs = tx?.meta?.logMessages ?? [];
      throw new SimulationError(
        `${label} landed on-chain but FAILED: ${JSON.stringify(v.err)}`,
        logs
      );
    }
    if (v?.confirmationStatus === "confirmed" || v?.confirmationStatus === "finalized") return;
    await new Promise((r) => setTimeout(r, 1000));
  }
  throw new Error(`${label}: could not confirm ${signature} within 20s`);
}

/**
 * cp-swap rejects any swap with NotApproved (0x1770) until the cluster clock is
 * past the pool's open_time, which initialize() sets from the block timestamp
 * at creation. Swapping seconds after opening a pool therefore fails, and it
 * fails in a way that looks like a broken pool rather than a race.
 */
export async function waitForPoolOpen(
  connection: Connection,
  openTimeSeconds: number,
  onProgress?: (msg: string) => void
): Promise<void> {
  const deadline = Date.now() + 180_000;
  while (Date.now() < deadline) {
    const slot = await connection.getSlot("confirmed");
    const now = await connection.getBlockTime(slot);
    if (now !== null && now > openTimeSeconds) return;
    onProgress?.(
      `waiting ${now === null ? "?" : openTimeSeconds - now + 1}s for the pool to open`
    );
    await new Promise((r) => setTimeout(r, 2000));
  }
  throw new Error(`pool open_time ${openTimeSeconds} was not reached within 180s`);
}

/**
 * Sign a wave of transactions with ONE wallet approval, then send and confirm
 * them.
 *
 * Why waves rather than signing everything up front: a blockhash is valid for
 * roughly 150 slots, about a minute. Signing two hundred transactions and
 * sending them over ten minutes means the tail expires with
 * `BlockhashNotFound`, after the user has already approved them. So each wave
 * takes a fresh blockhash and is kept small enough to land inside its life.
 *
 * Returns one result per input, in order, so the caller can mark exactly which
 * lines went through. A failure in one transaction never discards the others.
 */
export async function signAndSendWave(opts: {
  connection: Connection;
  wallet: ConnectedWallet;
  fees: FeeSettings;
  batches: { instructions: TransactionInstruction[] }[];
  onProgress?: (m: string) => void;
}): Promise<{ signature?: string; error?: string }[]> {
  const { connection, wallet, fees, batches, onProgress } = opts;
  if (!batches.length) return [];

  const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash("confirmed");

  const txs: VersionedTransaction[] = batches.map((b) => {
    const message = new TransactionMessage({
      payerKey: wallet.publicKey,
      recentBlockhash: blockhash,
      instructions: [
        ComputeBudgetProgram.setComputeUnitLimit({ units: fees.computeUnitLimit }),
        ComputeBudgetProgram.setComputeUnitPrice({
          microLamports: fees.computeUnitPriceMicroLamports,
        }),
        ...b.instructions,
      ],
    }).compileToV0Message();
    return new VersionedTransaction(message);
  });

  onProgress?.(`Requesting one signature for ${txs.length} transaction(s)`);
  const signed = await wallet.signAllTransactions(txs);

  const out: { signature?: string; error?: string }[] = [];
  // Send them all before confirming any: confirmation is the slow part, and
  // serialising it would push the last transactions past their blockhash.
  const inflight: { index: number; signature?: string; error?: string }[] = [];
  for (const [i, tx] of signed.entries()) {
    try {
      const sig = await connection.sendRawTransaction(
        (tx as VersionedTransaction).serialize(),
        { preflightCommitment: "confirmed", skipPreflight: false }
      );
      inflight.push({ index: i, signature: sig });
    } catch (e) {
      inflight.push({ index: i, error: (e as Error).message });
    }
  }

  for (const item of inflight) {
    if (!item.signature) {
      out[item.index] = { error: item.error };
      continue;
    }
    try {
      await connection.confirmTransaction(
        { signature: item.signature, blockhash, lastValidBlockHeight },
        "confirmed"
      );
      // Landed is not succeeded — the same trap as everywhere else here.
      const status = await connection.getSignatureStatus(item.signature, {
        searchTransactionHistory: true,
      });
      if (status.value?.err) {
        out[item.index] = { error: `failed on-chain: ${JSON.stringify(status.value.err)}` };
      } else {
        out[item.index] = { signature: item.signature };
      }
    } catch (e) {
      out[item.index] = { error: (e as Error).message };
    }
  }
  return out;
}
